import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Running Studio tools, including Content Plan, which first asks for a
 * platform in the social platform modal.
 */
export function useStudioTools(projectId: string | undefined) {
	const { i18n } = useTranslation();
	const queryClient = useQueryClient();
	const [loadingTool, setLoadingTool] = useState<string | null>(null);
	const [socialModalOpen, setSocialModalOpen] = useState(false);
	const [selectedSocials, setSelectedSocials] = useState<string[]>([]);

	const handleToolClick = async (toolSlug: string | null) => {
		if (!toolSlug || !projectId) return;
		setLoadingTool(toolSlug);
		try {
			await api.runCanvasTool(
				toolSlug,
				projectId,
				(i18n.resolvedLanguage ?? i18n.language ?? "ru") as "en" | "ru",
			);
			toast.success("Processing started");
			queryClient.invalidateQueries({ queryKey: ["toolResults", projectId] });
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to start processing",
			);
		} finally {
			setLoadingTool(null);
		}
	};

	const openContentPlan = () => {
		setSelectedSocials([]);
		setSocialModalOpen(true);
	};

	const runContentPlan = async () => {
		const platform = selectedSocials[0];
		setSocialModalOpen(false);
		setLoadingTool("content-plan");
		try {
			await api.runCanvasTool(
				"content-plan",
				projectId!,
				(i18n.resolvedLanguage ?? i18n.language ?? "ru") as "en" | "ru",
				platform,
			);
			toast.success("Content plan processing started");
			queryClient.invalidateQueries({
				queryKey: ["toolResults", projectId],
			});
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to start content plan",
			);
		} finally {
			setLoadingTool(null);
		}
	};

	return {
		loadingTool,
		socialModalOpen,
		setSocialModalOpen,
		selectedSocials,
		setSelectedSocials,
		handleToolClick,
		openContentPlan,
		runContentPlan,
	};
}
